import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Trash2, Download } from 'lucide-react';
import api, { uploadRequestConfig } from '../../services/api';
import CommentThread from '../common/CommentThread';
import StatusBadge from '../common/StatusBadge';
import PriorityBadge from '../common/PriorityBadge';
import FileUploader from '../common/FileUploader';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { getUserIdentifier, normalizeUserProfiles } from '../../utils/userProfile';

const TaskDetail = ({ onSidebarCountsRefresh }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [commentData, setCommentData] = useState({
    message: '',
    updateStatus: ''
  });
  const [files, setFiles] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddAssignee, setShowAddAssignee] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [addingAssignees, setAddingAssignees] = useState(false);

  useEffect(() => {
    fetchTaskDetails();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when task id changes
  }, [id]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/admin/employees');
      setEmployees(normalizeUserProfiles(response.data.employees || []));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTaskDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/tasks/${id}`);
      setTask(response.data.task);
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast.error('Failed to fetch task details');
      navigate('/admin/tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!commentData.message.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append('message', commentData.message.trim());
      
      if (commentData.updateStatus) {
        formData.append('updateStatus', commentData.updateStatus);
      }

      files.forEach(file => {
        formData.append('attachments', file);
      });

      const response = await api.post(`/admin/tasks/${id}/comments`, formData, uploadRequestConfig());

      setTask(response.data.task);
      setCommentData({ message: '', updateStatus: '' });
      setFiles([]);
      if (onSidebarCountsRefresh) {
        onSidebarCountsRefresh();
      }
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(error.response?.data?.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTask = async () => {
    try {
      await api.delete(`/admin/tasks/${id}`);
      if (onSidebarCountsRefresh) {
        onSidebarCountsRefresh();
      }
      toast.success('Task deleted successfully');
      navigate('/admin/tasks');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleAddAssignees = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    try {
      setAddingAssignees(true);
      const response = await api.put(`/admin/tasks/${id}/assignees`, {
        assigneeIds: selectedEmployees
      });
      setTask(response.data.task);
      setSelectedEmployees([]);
      setShowAddAssignee(false);
      toast.success('Assignees added successfully');
    } catch (error) {
      console.error('Error adding assignees:', error);
      toast.error(error.response?.data?.message || 'Failed to add assignees');
    } finally {
      setAddingAssignees(false);
    }
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const getAvailableEmployees = () => {
    if (!task || !employees) return [];
    const assignedIds = task.assignedTo.map((emp) => getUserIdentifier(emp)).filter(Boolean);
    return employees.filter((emp) => {
      const employeeKey = getUserIdentifier(emp);
      return employeeKey && !assignedIds.includes(employeeKey);
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = task?.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!task) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4 flex-1">
          <button
            onClick={() => navigate('/admin/tasks')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">{task.title}</h1>
              <StatusBadge status={task.status} reviewDecision={task.reviewDecision} />
              <PriorityBadge priority={task.priority} />
            </div>
            {isOverdue && (
              <div className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-2">
                Overdue
              </div>
            )}
            <div className="text-sm text-gray-600 space-y-1">
              <p>Created by: <span className="font-medium">{task.assignedBy?.name}</span></p>
              <p>Created on: {formatDate(task.createdAt)}</p>
              {task.dueDate && (
                <p>Due: <span className={isOverdue ? 'text-red-600 font-medium' : ''}>{formatDate(task.dueDate)}</span></p>
              )}
              {task.completedAt && (
                <p>Completed on: {formatDate(task.completedAt)}</p>
              )}
            </div>
          </div>
        </div>

        {/* Delete Button */}
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete Task"
        >
          <Trash2 className="h-5 w-5" />
        </button>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Task Details & Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Task Description */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Description</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 whitespace-pre-wrap">{task.description}</p>

              {/* Initial Attachments */}
              {task.initialAttachments && task.initialAttachments.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Initial Attachments:</h4>
                  <div className="space-y-2">
                    {task.initialAttachments.map((file, index) => (
                      <a
                        key={index}
                        href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/uploads/tasks/${file.filename}`}
                        download={file.originalName}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 text-sm group"
                      >
                        <Download className="h-4 w-4" />
                        <span className="group-hover:underline">{file.originalName}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Activity & Comments */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Activity & Comments</h3>
            </div>
            <div className="p-6">
              <CommentThread
                comments={task.comments}
                statusHistory={task.statusHistory}
                currentUser={user}
              />
            </div>
          </div>

          {/* Add Comment Form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Comment</h3>
            </div>
            <form onSubmit={handleCommentSubmit} className="p-6 space-y-4">
              {/* Comment Textarea */}
              <div>
                <textarea
                  value={commentData.message}
                  onChange={(e) => setCommentData({ ...commentData, message: e.target.value })}
                  placeholder="Add your feedback, instructions, or updates..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>

              {/* File Upload */}
              <FileUploader
                files={files}
                onFilesChange={setFiles}
                maxFiles={3}
                label="Attach Files (Optional)"
              />

              {/* Status Update */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Update Task Status (Optional)
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="statusUpdate"
                      value=""
                      checked={commentData.updateStatus === ''}
                      onChange={(e) => setCommentData({ ...commentData, updateStatus: e.target.value })}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Keep current status</span>
                  </label>

                  <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="statusUpdate"
                      value="incomplete"
                      checked={commentData.updateStatus === 'incomplete'}
                      onChange={(e) => setCommentData({ ...commentData, updateStatus: e.target.value })}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Reject &amp; Send Back</span>
                      <p className="text-xs text-gray-500">Send task back to employee for rework</p>
                    </div>
                  </label>

                  <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="statusUpdate"
                      value="completed"
                      checked={commentData.updateStatus === 'completed'}
                      onChange={(e) => setCommentData({ ...commentData, updateStatus: e.target.value })}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Mark as Complete</span>
                      <p className="text-xs text-gray-500">Approve and close this task</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting || !commentData.message.trim()}
                  className="flex items-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Posting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Post Comment
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column - Assignees */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Assigned To ({task.assignedTo?.length || 0})
              </h3>
              {task.status !== 'completed' && (
                <button
                  onClick={() => setShowAddAssignee(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  + Add More
                </button>
              )}
            </div>
            <div className="p-6 space-y-3">
              {task.assignedTo && task.assignedTo.length > 0 ? (
                task.assignedTo.map((employee) => (
                  <div
                    key={getUserIdentifier(employee) || employee.email}
                    className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold">
                      {employee.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {employee.name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {employee.email}
                      </p>
                      {employee.employeeId && (
                        <p className="text-xs text-gray-400">
                          ID: {employee.employeeId}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No assignees</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Assignees Modal */}
      {showAddAssignee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add Assignees</h3>
            
            {getAvailableEmployees().length > 0 ? (
              <>
                <div className="space-y-2 mb-6">
                  {getAvailableEmployees().map((employee) => (
                    (() => {
                      const employeeKey = getUserIdentifier(employee);
                      if (!employeeKey) return null;
                      return (
                    <label
                      key={employeeKey}
                      className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(employeeKey)}
                        onChange={() => toggleEmployeeSelection(employeeKey)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 rounded"
                      />
                      <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                        {employee.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{employee.name}</p>
                        <p className="text-sm text-gray-500 truncate">{employee.email}</p>
                        {employee.employeeId && (
                          <p className="text-xs text-gray-400">ID: {employee.employeeId}</p>
                        )}
                      </div>
                    </label>
                      );
                    })()
                  ))}
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowAddAssignee(false);
                      setSelectedEmployees([]);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    disabled={addingAssignees}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddAssignees}
                    disabled={addingAssignees || selectedEmployees.length === 0}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingAssignees ? (
                      <>
                        <LoadingSpinner size="sm" className="inline mr-2" />
                        Adding...
                      </>
                    ) : (
                      `Add ${selectedEmployees.length} Employee${selectedEmployees.length !== 1 ? 's' : ''}`
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600 mb-6">
                  All employees are already assigned to this task.
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowAddAssignee(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Task?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this task? This action cannot be undone and all associated comments and files will be permanently deleted.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTask}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskDetail;
